import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AlertService } from '@ose/services';

@Component({
  selector: 'lib-file-upload',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class FileUploadComponent {
  @Input() canPaste = false;

  @Input() readonly = false;

  @Input() uploadPercent = -1;

  @Input() parentSection = '';

  @Input() image?: string | null;

  @Input() loading = false;

  @Input() multiple = false;

  @Input() uploadTitle = 'FILE_UPLOAD.UploadTitle';

  @Input() uploadText = 'FILE_UPLOAD.UploadText';

  @Input() uploadedText = 'FILE_UPLOAD.UploadedText';

  @Output() fileChange = new EventEmitter<void>();

  @ViewChild('fileInput')
  fileInput?: ElementRef<HTMLInputElement>;

  files: File[] = [];

  private readonly translateService = inject(TranslateService);

  private readonly alertService = inject(AlertService);

  private readonly cdr = inject(ChangeDetectorRef);

  imageClear(): void {
    this.image = null;

    this.files = [];

    this.uploadPercent = -1;

    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }

    this.fileChange.emit();

    this.cdr.detectChanges();
  }

  protected async onFileChanged(event?: Event): Promise<void> {
    this.uploadPercent = -1;

    const input = event?.target as HTMLInputElement | null;

    const selectedFiles = input?.files;

    if (!event || !selectedFiles || selectedFiles.length === 0) {
      return;
    }

    const file = selectedFiles[0];

    if (file.type === 'application/x-zip-compressed') {
      event.preventDefault();

      this.showUnsupportedFileToast();

      input.value = '';

      return;
    }

    await this.setFile(file);
  }

  protected async onPaste(event: ClipboardEvent): Promise<void> {
    if (!this.canPaste || this.readonly || this.loading || this.image) {
      return;
    }

    const clipboardData = event.clipboardData;

    if (!clipboardData) {
      return;
    }

    const items = Array.from(clipboardData.items);

    const imageItem = items.find(
      (item) => item.kind === 'file' && item.type.startsWith('image/')
    );

    if (!imageItem) {
      return;
    }

    const pastedFile = imageItem.getAsFile();

    if (!pastedFile) {
      return;
    }

    event.preventDefault();

    const file = this.createClipboardFile(pastedFile, pastedFile.type);

    await this.setFile(file);
  }

  private async setFile(file: File): Promise<void> {
    this.uploadPercent = -1;

    this.files = [file];

    if (file.type.startsWith('image/')) {
      try {
        this.image = await this.getBase64(file);

        const resizedFile = await this.resizeImage(file, 1000, 1000);

        this.files = [resizedFile];
      } catch (error: unknown) {
        console.error('Image processing failed', error);

        this.image = null;

        this.files = [];

        this.showUnsupportedFileToast();

        this.cdr.detectChanges();

        return;
      }
    }

    this.fileChange.emit();

    this.cdr.detectChanges();
  }

  private createClipboardFile(blob: Blob, mimeType: string): File {
    const extension = this.getImageExtension(mimeType);

    return new File([blob], `clipboard-image-${Date.now()}.${extension}`, {
      type: mimeType,
      lastModified: Date.now(),
    });
  }

  private getImageExtension(mimeType: string): string {
    switch (mimeType) {
      case 'image/jpeg':
      case 'image/jpg':
        return 'jpg';

      case 'image/png':
        return 'png';

      case 'image/webp':
        return 'webp';

      default:
        return 'png';
    }
  }

  private showUnsupportedFileToast(): void {
    this.alertService.showToast({
      body: this.translateService.instant('FILE_UPLOAD.FileNotSupported'),
      type: 'danger',
    });
  }

  private getBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        resolve(reader.result as string);
      };

      reader.onerror = () => {
        reject(reader.error);
      };

      reader.readAsDataURL(file);
    });
  }

  private loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      const image = new Image();

      reader.onload = () => {
        image.src = reader.result as string;

        image.onload = () => {
          resolve(image);
        };

        image.onerror = () => {
          reject(new Error('Image could not be loaded.'));
        };
      };

      reader.onerror = () => {
        reject(reader.error);
      };

      reader.readAsDataURL(file);
    });
  }

  private async resizeImage(
    file: File,
    maxWidth: number,
    maxHeight: number
  ): Promise<File> {
    const image = await this.loadImage(file);

    const canvas = document.createElement('canvas');

    const context = canvas.getContext('2d');

    if (!context) {
      throw new Error('Canvas context is not available.');
    }

    const scaleFactor = Math.min(
      1,
      maxWidth / image.width,
      maxHeight / image.height
    );

    canvas.width = Math.round(image.width * scaleFactor);

    canvas.height = Math.round(image.height * scaleFactor);

    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, file.type, 0.9);
    });

    if (!blob) {
      throw new Error('Resized image could not be created.');
    }

    return new File([blob], file.name, {
      type: file.type,
      lastModified: file.lastModified,
    });
  }
}
